import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import { useDialog } from '@setvi/shared/providers';

import { ROUTES } from 'enumsV2';
import { sanitizeUrl } from 'Utils/sanitizeUrl';

import { PrintOrderItem } from '../../../types';
import { usePrintOrderStore } from '../../../store';
import { usePrintOrderCart } from '../../../hooks/use-print-order-cart';
import { getItemPageCount, openCheckoutTab } from '../../../utils';
import { CustomizeDialog } from '../../customize-dialog';
import { LocationDialog } from '../../location-dialog';
import { CUSTOMIZE_DIALOG_ID, LOCATION_DIALOG_ID } from '../../../constants';

export const useCartDialog = (onClose: () => void) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { openDialog, closeDialog } = useDialog();
  const items = usePrintOrderStore(state => state.items);
  const { removeItem, checkout, isCheckingOut } = usePrintOrderCart();
  // Id of the item currently being removed (drives its button spinner).
  const [removingId, setRemovingId] = useState<number | null>(null);

  const handleRemove = async (id: number) => {
    setRemovingId(id);
    try {
      await removeItem(id);
    } catch {
      // error toast handled globally by the mutation cache
    } finally {
      setRemovingId(null);
    }
    // Removing the last item leaves nothing to review — close the dialog.
    if (!usePrintOrderStore.getState().items.length) onClose();
  };

  const handleCheckout = async () => {
    // The portal tab MUST open synchronously on the click — after the await,
    // popup blockers silently drop window.open (this is also how main behaves;
    // the tab shows a "connecting" note until the response redirects it).
    const proofTab = openCheckoutTab();

    try {
      const result = await checkout();

      // No server cart id yet (a sync is still in flight or has failed) —
      // nothing was ordered, so don't report success or leave the dialog.
      if (!result) {
        proofTab.close();
        enqueueSnackbar('Your cart is still saving — please try again.', {
          variant: 'warning'
        });
        return;
      }
      // External order/proof portal URL returned by the API.
      const redirectUrl = result?.RedirectUrl ?? result?.Cart?.Url;

      if (redirectUrl) proofTab.redirect(sanitizeUrl(redirectUrl));
      else proofTab.close();

      enqueueSnackbar('Order sent to print.', { variant: 'success' });
      onClose();
      // Send the user to Order History in our app.
      navigate(ROUTES.SHOPPING_CART);
    } catch {
      // Checkout failed — close the waiting tab and keep the dialog open; the
      // mutation cache toasts the error.
      proofTab.close();
    }
  };

  const totalPages = useMemo(
    () => items.reduce((sum, item) => sum + getItemPageCount(item), 0),
    [items]
  );

  // Edit hands over to the item's own dialog; this one closes first so the
  // two never stack.
  const openCustomize = (item: PrintOrderItem) => {
    onClose();

    // Location items are edited in their own dialog.
    if (item.addLocation) {
      openDialog(
        <LocationDialog
          resource={item.resource}
          initial={{
            addLocation: item.addLocation,
            locationId: item.locationId,
            locationFileName: item.locationFileName,
            locationPreviewUrl: item.locationPreviewUrl
          }}
          confirmLabel="Save"
          isEdit
          onClose={() => closeDialog(LOCATION_DIALOG_ID)}
        />,
        { id: LOCATION_DIALOG_ID }
      );
      return;
    }

    openDialog(
      <CustomizeDialog
        resource={item.resource}
        initial={{
          addQr: item.addQr,
          qrNumbers: item.qrNumbers,
          qrFileName: item.qrFileName,
          qrSheetUrl: item.qrSheetUrl,
          addCover: item.addCover,
          coverTemplateId: item.coverTemplateId,
          coverFileName: item.coverFileName,
          coverPreviewUrl: item.coverPreviewUrl,
          coverFields: item.coverFields
        }}
        confirmLabel="Save"
        isEdit
        onClose={() => closeDialog(CUSTOMIZE_DIALOG_ID)}
      />,
      { id: CUSTOMIZE_DIALOG_ID }
    );
  };

  return {
    items,
    removingId,
    handleRemove,
    handleCheckout,
    isCheckingOut,
    totalPages,
    openCustomize
  };
};
